import type { FormEvent, ReactNode } from "react";
import { createElement, useState } from "react";
import { cn, tv } from "tailwind-variants";
import type {
	AnimationScrollType,
	HeadingLevel,
	ReadWidthLength,
} from "#/_packages/umbrella/types";
import { m } from "#/paraglide/messages";

export type SearchBarIntent =
	| "primary"
	| "secondary"
	| "light"
	| "dark"
	| "outline"
	| "white";

export type SearchBarProps = {
	headingLabel?: string;
	heading?: string;
	headingLevel?: HeadingLevel;
	body?: ReactNode;
	intentColor?: SearchBarIntent;
	placeholder?: string;
	searchLabel?: string;
	searchPageUrl?: string;
	customSearchEngine?: boolean;
	customUrl?: string;
	customTargetBlank?: boolean;
	onSearch?: (query: string) => void;
	id?: string;
	readWidthToggle?: boolean;
	readWidthLength?: ReadWidthLength;
	animationHoverType?: string | null;
	animationScrollType?: AnimationScrollType;
	className?: string;
};

const searchBar = tv({
	slots: {
		input: "form-control form-control-search w-full",
		iconButton: "btn search-bar-btn-search",
	},
});

function SearchIcon() {
	return (
		<svg className="icon icon-24 size-6" aria-hidden="true">
			<use href="/umbrella/ui/icons/lucide-search.svg#icon" />
		</svg>
	);
}

function toIntentClass(intentColor: SearchBarIntent) {
	switch (intentColor) {
		case "primary":
			return "search-bar-intent-primary";
		case "secondary":
			return "search-bar-intent-secondary";
		case "light":
			return "search-bar-intent-light";
		case "dark":
			return "search-bar-intent-dark";
		case "outline":
			return "search-bar-intent-outline";
		case "white":
			return "search-bar-intent-white";
		default:
			return "";
	}
}

function toReadWidthLengthClass(length: ReadWidthLength) {
	switch (length) {
		case "narrow":
			return "read-width-narrow";
		case "wide":
			return "read-width-wide";
		default:
			return "read-width-normal";
	}
}

function resolveSearchUrl({
	query,
	customSearchEngine,
	customUrl,
	searchPageUrl,
}: {
	query: string;
	customSearchEngine?: boolean;
	customUrl?: string;
	searchPageUrl?: string;
}) {
	if (customSearchEngine && customUrl?.includes("%s")) {
		return customUrl.replace("%s", encodeURIComponent(query));
	}

	if (!searchPageUrl) {
		return null;
	}

	const separator = searchPageUrl.includes("?") ? "&" : "?";
	return `${searchPageUrl}${separator}q=${encodeURIComponent(query)}`;
}

export function SearchBar({
	headingLabel,
	heading,
	headingLevel = 2,
	body,
	intentColor = "primary",
	placeholder,
	searchLabel,
	searchPageUrl = "/search",
	customSearchEngine = false,
	customUrl,
	customTargetBlank = false,
	onSearch,
	id,
	readWidthToggle = false,
	readWidthLength = "normal",
	animationHoverType,
	animationScrollType = "none",
	className,
}: SearchBarProps) {
	const [query, setQuery] = useState("");
	const resolvedPlaceholder = placeholder ?? m.search();
	const resolvedSearchLabel = searchLabel ?? m.search();

	const HeadingTag = `h${headingLevel}`;
	const { input, iconButton } = searchBar();
	const useCustomRedirect =
		customSearchEngine && Boolean(customUrl?.includes("%s"));
	const formAction = useCustomRedirect ? "" : searchPageUrl;

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const trimmed = query.trim();
		if (!trimmed) {
			return;
		}

		onSearch?.(trimmed);

		const targetUrl = resolveSearchUrl({
			query: trimmed,
			customSearchEngine,
			customUrl,
			searchPageUrl,
		});

		if (!targetUrl) {
			return;
		}

		if (useCustomRedirect && customTargetBlank) {
			window.open(targetUrl, "_blank", "noopener,noreferrer");
			return;
		}

		window.location.assign(targetUrl);
	};

	return (
		<div
			id={id}
			className={cn(
				"block__search-bar w-full",
				toIntentClass(intentColor),
				animationHoverType,
				animationScrollType !== "none" && animationScrollType,
				className,
			)}
		>
			<div
				className={cn(
					readWidthToggle && "read-width",
					readWidthToggle && toReadWidthLengthClass(readWidthLength),
				)}
			>
				{headingLabel || heading ? (
					<header>
						{headingLabel ? (
							<span className="kicker">{headingLabel}</span>
						) : null}
						{heading
							? createElement(
									HeadingTag,
									{
										className: "h3",
									},
									heading,
								)
							: null}
					</header>
				) : null}

				<form
					action={formAction}
					method="get"
					data-custom-url={useCustomRedirect ? customUrl : undefined}
					data-target={
						useCustomRedirect
							? customTargetBlank
								? "blank"
								: "self"
							: undefined
					}
					onSubmit={handleSubmit}
				>
					<div className="search-bar-inner">
						<button type="submit" className={iconButton()}>
							<SearchIcon />
							<span className="sr-only">{resolvedSearchLabel}</span>
						</button>
						<input
							type="search"
							name="q"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							className={input()}
							placeholder={resolvedPlaceholder}
							aria-label={resolvedPlaceholder}
						/>
					</div>
				</form>

				{body ? <div className="form-text mb-0-last">{body}</div> : null}
			</div>
		</div>
	);
}
